import React from 'react';

import DSPTableRow from './DSPTableRow';
import {
  TableWithMinWidth,
  TableBodyBottomBorder,
} from '../shared/Charts/styles';
import ChartTableHeader from '../shared/Charts/ChartTableHeaders';
import LocalChartRows from './LocalChartRows';
import {
  TableRow,
  TableCell,
} from 'components/data-containers/PositionsTable/shared/PositionsTable.style';
import TableLoader from 'components/utility/ContentLoader/TableLoader/TableLoader';
import Layout from 'components/styled/Layout/Layout';
import BreakdownTablePlaceholder from 'components/data-visualization/Table/BreakdownTablePlaceholder';
import TableBody from 'components/data-visualization/Table/Body';
import { IChartItem } from './types';

interface IGlobalChartRowsProps {
  globalCharts: IChartItem[];
  isSmallScreen: boolean;
  productType: string;
  showProductColumn: boolean;
}

const GlobalChartRows: React.FC<IGlobalChartRowsProps> = ({
  globalCharts,
  isSmallScreen,
  productType,
  showProductColumn,
}) => {
  if (!globalCharts || !globalCharts.length) {
    return null;
  }
  return (
    <TableBodyBottomBorder>
      {globalCharts.map(
        (chart: IChartItem): JSX.Element => (
          <DSPTableRow
            key={`global-chart-row-${chart.chartId}-${chart.productId}`}
            chart={chart}
            isSmallScreen={isSmallScreen}
            productType={productType}
            showProductColumn={showProductColumn}
          />
        )
      )}
    </TableBodyBottomBorder>
  );
};

interface IDSPChartsTableProps {
  charts: IChartItem[];
  globalCharts: IChartItem[];
  isSmallScreen: boolean;
  productType: string;
  loading: boolean;
  showProductColumn: boolean;
}

/**
 * Table for the overview DSP charts, such as 'Spotify Top 200 Weekly'.
 *
 * @param {Array} charts all the charts to render
 * @param {Boolean} isSmallScreen match media breakpoint
 * @param {Array} globalCharts filtered global charts from `charts`
 * @param {Object} productType value and label for product (track/album/etc)
 * @param {bool} loading Indicates whether the component should be rendered in its loading state
 * @param {bool} showProductColumn Indicates whetehr the product column will be rendered or not
 */
const DSPChartsTable: React.FC<IDSPChartsTableProps> = (props) => {
  const {
    charts,
    globalCharts,
    isSmallScreen,
    productType,
    loading,
    showProductColumn,
  } = props;

  const hasData =
    !loading &&
    charts &&
    globalCharts &&
    charts.length + globalCharts.length > 0;

  return (
    <Layout.Relative>
      <TableWithMinWidth wide={showProductColumn}>
        <ChartTableHeader
          isSmallScreen={isSmallScreen}
          showProductColumn={showProductColumn}
          firstColumnAlign="left"
        />
        {loading && (
          <TableBody>
            <TableLoader
              columnCount={showProductColumn ? 4 : 3}
              rowComponent={TableRow}
              cellComponent={TableCell}
            />
          </TableBody>
        )}
        {hasData && (
          <>
            <GlobalChartRows
              globalCharts={globalCharts}
              isSmallScreen={isSmallScreen}
              productType={productType}
              showProductColumn={showProductColumn}
            />
            <LocalChartRows
              charts={charts}
              isSmallScreen={isSmallScreen}
              productType={productType}
              showProductColumn={showProductColumn}
            />
          </>
        )}
        {!loading && !hasData && (
          <TableBody>
            <BreakdownTablePlaceholder
              isSmallScreen={isSmallScreen}
              columnCount={showProductColumn ? 4 : 3}
            />
          </TableBody>
        )}
      </TableWithMinWidth>
    </Layout.Relative>
  );
};

export default DSPChartsTable;
